<template>
  <div class="plot-page">
    <div class="container mt-4" v-if="plot">
      <!-- Header -->
      <div class="plot-header mb-4">
        <div class="d-flex justify-content-between align-items-center">
          <h1 class="plot-title">Plot #{{ plot.tokenId }}</h1>
          <router-link to="/" class="btn btn-outline-dark">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
            </svg>
            Back to Homepage
          </router-link>
        </div>
      </div>

      <div class="row">
        <!-- Left Column - Image -->
        <div class="col-lg-5 mb-4">
          <div class="plot-image-container">
            <img v-bind:src="'https://millionbithomepage.com/seq/'+plot.seq+'/token.jpeg'"
                 class="plot-image img-fluid"
                 alt="Plot Image" />
          </div>
          
          <!-- Zoom Image Card (conditionally shown) -->
          <div class="plot-image-container mt-4" v-if="showZoomImage">
            <img v-bind:src="'https://millionbithomepage.com/seq/'+plot.seq+'/zoom.gif'"
                 class="plot-image img-fluid"
                 alt="Plot Zoom Image"
                 @load="onZoomImageLoad"
                 @error="onZoomImageError"
                 v-show="zoomImageLoaded" />
            <div v-if="zoomImageLoading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="sr-only">Loading zoom...</span>
              </div>
              <p class="mt-2 mb-0 text-muted small">Loading zoom view...</p>
            </div>
          </div>
        </div>

        <!-- Right Column - Details -->
        <div class="col-lg-7">
          <div class="plot-details">
            
            <!-- URL Section -->
            <div class="detail-section mb-4">
              <h5 class="section-title">Link</h5>
              <div class="website-link">
                <a v-bind:href="plot.url" target="_blank" class="btn btn-primary btn-lg">
                  Visit Site
                </a>
                <div class="url-text mt-2">{{ plot.url }}</div>
              </div>
            </div>

            <!-- Stats Grid -->
            <div class="detail-section mb-4">
              <h5 class="section-title">Plot Statistics</h5>
              <div class="stats-grid">
                <div class="stat-card">
                  <div class="stat-content">
                    <div class="stat-label">Pixels</div>
                    <div class="stat-value">{{ plot.pixels.toLocaleString() }}</div>
                  </div>
                </div>
                
                <div class="stat-card">
                  <div class="stat-content">
                    <div class="stat-label">Location</div>
                    <div class="stat-value">({{ plot.x1 }}, {{ plot.y1 }}) to ({{ plot.x2 }}, {{ plot.y2 }})</div>
                  </div>
                </div>
                
                <div class="stat-card">
                  <div class="stat-content">
                    <div class="stat-label">Updated</div>
                    <div class="stat-value">{{ new Date(plot.created).toLocaleDateString() }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Profile Section -->
            <div class="detail-section mb-4" v-if="plot.profile && hasProfileLinks">
              <h5 class="section-title">Profile Links</h5>
              <div class="profile-links">
                <a v-if="plot.profile.base_name"
                   v-bind:href="'https://base.org/name/'+plot.profile.base_name.replace('.base.eth', '')"
                   target="_blank"
                   class="profile-link base-link">
                  <svg width="16" height="16" viewBox="0 0 111 111" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M54.921 110.034C85.359 110.034 110.034 85.402 110.034 55.017C110.034 24.6319 85.359 0 54.921 0C26.0432 0 2.35281 22.1714 0 50.3923H72.8467V59.6416H3.9565e-07C2.35281 87.8625 26.0432 110.034 54.921 110.034Z" fill="#0052FF"/>
                  </svg>
                  <span>Base Name</span>
                </a>
                
                <a v-if="plot.profile.website_url && plot.profile.website_url !== plot.url"
                   v-bind:href="plot.profile.website_url"
                   target="_blank"
                   class="profile-link website-link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/>
                    <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z"/>
                  </svg>
                  <span>Website</span>
                </a>
                
                <a v-if="plot.profile.x_username"
                   v-bind:href="'https://x.com/'+plot.profile.x_username"
                   target="_blank"
                   class="profile-link twitter-link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/>
                  </svg>
                  <span>@{{ plot.profile.x_username }}</span>
                </a>
                
                <a v-if="plot.profile.tg_url"
                   v-bind:href="plot.profile.tg_url"
                   target="_blank"
                   class="profile-link telegram-link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8.287 5.906q-1.168.486-4.666 2.01-.567.225-.595.442c-.03.243.275.339.69.47l.175.055c.408.133.958.288 1.243.294q.39.01.868-.32 3.269-2.206 3.374-2.23c.05-.012.12-.026.166.016s.042.12.037.141c-.03.129-1.227 1.241-1.846 1.817-.193.18-.33.307-.358.336a8 8 0 0 1-.188.186c-.38.366-.664.64.015 1.088.327.216.589.393.85.571.284.194.568.387.936.629q.14.092.27.187c.331.236.63.448.997.414.214-.02.435-.22.547-.82.265-1.417.786-4.486.906-5.751a1.4 1.4 0 0 0-.013-.315.34.34 0 0 0-.114-.217.53.53 0 0 0-.31-.093c-.3.005-.763.166-2.984 1.09"/>
                  </svg>
                  <span>Telegram</span>
                </a>
                
                <a v-if="plot.profile.token_ca"
                   v-bind:href="'https://dexscreener.com/base/'+plot.profile.token_ca"
                   target="_blank"
                   class="profile-link chart-link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path fill-rule="evenodd" d="M0 0h1v15h15v1H0zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07"/>
                  </svg>
                  <span>Token Chart</span>
                </a>
                
                <a v-if="plot.profile.nft_ca"
                   v-bind:href="'https://magiceden.us/collections/base/'+plot.profile.nft_ca"
                   target="_blank"
                   class="profile-link nft-link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M4.502 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/>
                    <path d="M14.002 13a2 2 0 0 1-2 2h-10a2 2 0 0 1-2-2V5A2 2 0 0 1 2 3a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-1.998 2M14 2H4a1 1 0 0 0-1 1h9.002a2 2 0 0 1 2 2v7A1 1 0 0 0 15 11V3a1 1 0 0 0-1-1M2.002 4a1 1 0 0 0-1 1v8l2.646-2.354a.5.5 0 0 1 .63-.062l2.66 1.773 3.71-3.71a.5.5 0 0 1 .577-.094l1.777 1.947V5a1 1 0 0 0-1-1z"/>
                  </svg>
                  <span>NFT Collection</span>
                </a>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="detail-section">
              <h5 class="section-title">View On</h5>
              <div class="action-buttons">
                <a v-bind:href="'https://basescan.org/tx/'+plot.hash"
                   target="_blank"
                   class="btn btn-outline-primary">
                  <i class="bi bi-receipt"></i> BaseScan #{{ plot.seq }}
                </a>
                <a v-bind:href="'https://opensea.io/item/base/0x25b9afe64bb3593ec7e9dc7ef386a9b04c53f96e/'+plot.tokenId"
                   target="_blank"
                   class="btn btn-outline-primary">
                  <i class="bi bi-gem"></i> OpenSea #{{ plot.tokenId }}
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div class="container mt-4" v-else-if="loading">
      <div class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3">Loading plot information...</p>
      </div>
    </div>

    <!-- Not Found State -->
    <div class="container mt-4" v-else>
      <div class="not-found-container">
        <div class="alert alert-warning" role="alert">
          <h4 class="alert-heading">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
            </svg>
            Plot not found
          </h4>
          <p>The plot with ID <strong>{{ $route.params.id }}</strong> could not be found.</p>
          <hr>
          <router-link to="/" class="btn btn-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
            </svg>
            Back to Homepage
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*global axios*/
import utils from '../utils';

export default {
  name: 'plot',
  data () {
    return {
      plot: null,
      loading: true,
      transactions: [],
      s3TransactionsJson: utils.getTsPath('transactions.json'),
      showZoomImage: false,
      zoomImageLoaded: false,
      zoomImageLoading: false,
    };
  },
  computed: {
    hasProfileLinks() {
      if (!this.plot || !this.plot.profile) return false;
      const profile = this.plot.profile;
      return profile.base_name ||
             (profile.website_url && profile.website_url !== this.plot.url) ||
             profile.x_username ||
             profile.tg_url ||
             profile.token_ca ||
             profile.nft_ca;
    }
  },
  methods: {
    findPlotById: function(tokenId) {
      return this.transactions.find(t => t.tokenId == tokenId);
    },
    fetchData: async function() {
      var self = this;
      this.loading = true;
      
      try {
        const response = await axios.get(self.s3TransactionsJson);
        self.transactions = response.data;
        
        const plotId = self.$route.params.id;
        self.plot = self.findPlotById(plotId);
        
        // Update page title
        if (self.plot) {
          document.title = `Plot ${self.plot.tokenId} - Million Bit Homepage`;
          // Try to load zoom image
          self.tryLoadZoomImage();
        } else {
          document.title = `Plot ${plotId} - Not Found - Million Bit Homepage`;
        }
      } catch (error) {
        console.log('Error loading plot data:', error);
      } finally {
        this.loading = false;
      }
    },
    tryLoadZoomImage: function() {
      if (!this.plot) return;
      
      this.showZoomImage = true;
      this.zoomImageLoading = true;
      this.zoomImageLoaded = false;
      
      // Pre-load the image to check if it exists
      const img = new Image();
      const zoomUrl = `https://millionbithomepage.com/seq/${this.plot.seq}/zoom.gif`;
      
      img.onload = () => {
        this.onZoomImageLoad();
      };
      
      img.onerror = () => {
        this.onZoomImageError();
      };
      
      img.src = zoomUrl;
    },
    onZoomImageLoad: function() {
      this.zoomImageLoading = false;
      this.zoomImageLoaded = true;
    },
    onZoomImageError: function() {
      this.zoomImageLoading = false;
      this.zoomImageLoaded = false;
      this.showZoomImage = false;
    },
  },
  created: function () {
    this.fetchData();
  },
  watch: {
    // Watch for route changes to update the plot when navigating between plot pages
    '$route': function() {
      if (this.$route.name === 'plot') {
        this.fetchData();
      }
    }
  }
}
</script>

<style scoped>
.plot-page {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
  padding: 2rem 0;
}

.plot-header {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.plot-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #212529;
  margin: 0;
  background: linear-gradient(135deg, #007bff, #0056b3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.plot-image-container {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  text-align: center;
}

.plot-image {
  image-rendering: pixelated;
  border-radius: 8px;
  max-width: 100%;
  height: auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.plot-details {
  height: 100%;
}

.detail-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.website-link .btn {
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
}

/* .website-link .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
} */

.url-text {
  font-size: 0.9rem;
  color: #6c757d;
  word-break: break-all;
}

.zoom-title {
  color: #495057;
  font-weight: 600;
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 1rem;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.stat-card:hover {
  border-color: #007bff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.1);
}

.stat-icon {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  font-size: 1.1rem;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.8rem;
  color: #6c757d;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
}

.profile-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.profile-link {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 25px;
  text-decoration: none;
  color: #495057;
  font-weight: 500;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.profile-link:hover {
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.profile-link i, .profile-link svg {
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
}

.base-link:hover {
  background: #0052FF;
  border-color: #0052FF;
  color: white;
}

.twitter-link:hover {
  background: #1DA1F2;
  border-color: #1DA1F2;
  color: white;
}

.telegram-link:hover {
  background: #0088cc;
  border-color: #0088cc;
  color: white;
}

.chart-link:hover {
  background: #28a745;
  border-color: #28a745;
  color: white;
}

.nft-link:hover {
  background: #6f42c1;
  border-color: #6f42c1;
  color: white;
}


.action-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.action-buttons .btn {
  flex: 1;
  min-width: 200px;
  padding: 0.75rem 1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.action-buttons .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.2);
}

.action-buttons .btn i {
  margin-right: 0.5rem;
}

.loading-container, .not-found-container {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.loading-container p {
  color: #6c757d;
  font-size: 1.1rem;
  margin: 0;
}

@media (max-width: 768px) {
  .plot-title {
    font-size: 2rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .action-buttons .btn {
    min-width: auto;
  }
  
  .profile-links {
    justify-content: center;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Plot.vue?77cffdcc